import { Link } from "react-router-dom";
import { ShieldCheck, Phone, GraduationCap, CalendarCheck2, Handshake, BadgeCheck, ChevronRight, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import { SITE, SERVICE_AREAS } from "@/lib/site";

const SERVICES = [
  { icon: GraduationCap, title: "Homeowner education", desc: "Plainspoken information about residential heat pump options, how they work, and what to expect from an assessment." },
  { icon: CalendarCheck2, title: "Heat pump appointment coordination", desc: "We schedule no-obligation in-home appointments with a trusted HVAC partner that serves your area." },
  { icon: Handshake, title: "Partner HVAC company referrals", desc: "Interested homeowners are connected with vetted HVAC partner companies who handle assessments and installation." },
  { icon: BadgeCheck, title: "Representative accountability", desc: "Every representative is expected to identify themselves clearly, conduct themselves respectfully, and never pressure homeowners." },
];

const STEPS = [
  { n: "01", title: "A Maritime representative speaks with homeowners in the community" },
  { n: "02", title: "We ask basic questions about the home and current heating setup" },
  { n: "03", title: "If there is interest, we coordinate a no-obligation appointment" },
  { n: "04", title: "A partner HVAC company completes the assessment and provides any quote or recommendations" },
];

const STANDARDS = [
  "Clear identification",
  "Respectful conduct",
  "No pressure at the door",
  "No misleading claims",
  "Appointment booked only with homeowner consent",
  "Concerns are reviewed seriously",
];

export default function Home() {
  return (
    <>
      <Seo
        title="Maritime Thermal Solutions | Heat Pump Appointment Coordination in Nova Scotia"
        description="Maritime Thermal Solutions helps Nova Scotia homeowners learn about heat pump options and connect with trusted HVAC partners for no-obligation appointments."
      />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200 bg-white" data-testid="home-hero">
        <div className="container-x grid gap-10 py-14 md:grid-cols-12 md:gap-12 md:py-20 lg:py-24">
          <div className="md:col-span-7 lg:col-span-7">
            <p className="eyebrow" data-testid="hero-eyebrow">Nova Scotia · Homeowner Outreach</p>
            <h1 className="mt-3 font-heading text-4xl font-bold leading-[1.05] tracking-tight text-brand-navy sm:text-5xl lg:text-6xl" data-testid="hero-heading">
              Helping Nova Scotia Homeowners Explore Heat Pump Options
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg" data-testid="hero-subheading">
              Maritime Thermal Solutions is a local homeowner outreach and appointment-setting company.
              We help homeowners learn about residential heat pump options and connect interested
              homeowners with trusted HVAC partners in their area.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/verify" className="btn-primary" data-testid="hero-verify-cta">
                <ShieldCheck size={18} aria-hidden="true" />
                Verify a Representative
              </Link>
              <Link to="/contact" className="btn-outline" data-testid="hero-contact-cta">
                Contact Us
                <ChevronRight size={16} aria-hidden="true" />
              </Link>
            </div>
            <p className="mt-4 text-sm text-slate-500" data-testid="hero-trust-note">
              Serving homeowners across Nova Scotia.
            </p>
          </div>

          <div className="md:col-span-5 lg:col-span-5">
            <div className="relative overflow-hidden rounded-lg border border-slate-200 shadow-sm">
              <img
                src={SITE.heroImage}
                alt="Peggy's Cove lighthouse on the Nova Scotia coast"
                className="h-full w-full object-cover"
                loading="eager"
              />
            </div>
            <div className="mt-3 text-xs text-slate-500">
              Proudly serving communities across Nova Scotia.
            </div>
          </div>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="section-y bg-slate-50" data-testid="home-who-we-are">
        <div className="container-x grid items-start gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="eyebrow">Who We Are</p>
            <h2 className="mt-2 font-heading text-3xl font-semibold text-slate-900 sm:text-4xl">
              A local Halifax-based company, focused on homeowner education.
            </h2>
          </div>
          <div className="space-y-4 text-base leading-relaxed text-slate-600 lg:col-span-7">
            <p>
              Maritime Thermal Solutions is based in Halifax and works with homeowners
              across Nova Scotia. We focus on homeowner education and appointment coordination
              for residential heating and cooling solutions, with a particular focus on heat pumps.
            </p>
            <p>
              We do not install heat pumps ourselves. Our role is to help homeowners understand
              their options, answer basic questions, and — if there's interest — coordinate a
              no-obligation appointment with a trusted HVAC partner company that serves their area.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="section-y" data-testid="home-what-we-do">
        <div className="container-x">
          <div className="max-w-2xl">
            <p className="eyebrow">What We Do</p>
            <h2 className="mt-2 font-heading text-3xl font-semibold text-slate-900 sm:text-4xl">
              Education, coordination, and accountability.
            </h2>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {SERVICES.map((s) => (
              <div
                key={s.title}
                className="flex flex-col rounded-lg border border-slate-200 bg-white p-6 transition-shadow duration-200 hover:shadow-sm"
                data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-brand-navy/5 text-brand-navy">
                  <s.icon size={20} aria-hidden="true" />
                </div>
                <h3 className="mt-4 font-heading text-lg font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section-y bg-slate-50" data-testid="home-process">
        <div className="container-x">
          <div className="max-w-2xl">
            <p className="eyebrow">How the Process Works</p>
            <h2 className="mt-2 font-heading text-3xl font-semibold text-slate-900 sm:text-4xl">
              Four straightforward steps.
            </h2>
          </div>
          <ol className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((step) => (
              <li
                key={step.n}
                className="rounded-lg border border-slate-200 bg-white p-6"
                data-testid={`process-step-${step.n}`}
              >
                <div className="font-heading text-3xl font-bold text-brand-orange">{step.n}</div>
                <p className="mt-3 text-sm leading-relaxed text-slate-700">{step.title}</p>
              </li>
            ))}
          </ol>
          <div className="mt-8">
            <Link to="/how-it-works" className="inline-flex items-center gap-1 text-sm font-semibold text-brand-navy hover:text-brand-navy-hover" data-testid="process-readmore">
              Read the full process <ChevronRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* STANDARDS */}
      <section className="section-y" data-testid="home-standards">
        <div className="container-x grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="eyebrow">Our Standards</p>
            <h2 className="mt-2 font-heading text-3xl font-semibold text-slate-900 sm:text-4xl">
              How our representatives are expected to conduct themselves.
            </h2>
            <p className="mt-4 text-slate-600">
              We hold our representatives to a clear, written set of standards. If you've had a visit
              and something didn't feel right, we want to know.
            </p>
          </div>
          <ul className="grid gap-3 lg:col-span-7 sm:grid-cols-2" data-testid="standards-list">
            {STANDARDS.map((s) => (
              <li key={s} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
                <ShieldCheck size={18} className="mt-0.5 shrink-0 text-brand-navy" aria-hidden="true" />
                <span className="text-sm text-slate-700">{s}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* VERIFY CTA */}
      <section className="section-y bg-brand-navy text-white" data-testid="home-verify-cta">
        <div className="container-x grid items-center gap-8 md:grid-cols-12">
          <div className="md:col-span-8">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-orange">Verify a Representative</p>
            <h2 className="mt-2 font-heading text-3xl font-semibold sm:text-4xl">
              Someone came to your door? Confirm they're with us.
            </h2>
            <p className="mt-3 max-w-2xl text-white/85">
              If someone has visited your home and you would like to confirm they are associated with
              Maritime Thermal Solutions, you can contact us directly.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 md:col-span-4 md:justify-end">
            <Link to="/verify" className="btn-secondary" data-testid="home-verify-secondary-cta">
              <ShieldCheck size={18} aria-hidden="true" />
              Start verification
            </Link>
            <a href={SITE.phoneHref} className="inline-flex items-center gap-2 rounded-md border border-white/40 px-4 py-3 text-sm font-semibold text-white hover:bg-white/10" data-testid="home-call-cta">
              <Phone size={16} aria-hidden="true" />
              {SITE.phone}
            </a>
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="section-y" data-testid="home-service-area">
        <div className="container-x grid items-start gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="eyebrow">Service Area</p>
            <h2 className="mt-2 font-heading text-3xl font-semibold text-slate-900 sm:text-4xl">
              Serving communities across Nova Scotia.
            </h2>
            <p className="mt-4 text-slate-600">
              Maritime Thermal Solutions serves communities throughout Nova Scotia, including
              Halifax, Dartmouth, Bedford, Sackville, Truro, the Annapolis Valley, South Shore,
              North Shore, Cape Breton, and surrounding areas.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="service-area-list">
              {SERVICE_AREAS.map((area) => (
                <li key={area} className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700">
                  <MapPin size={14} className="text-brand-orange" aria-hidden="true" />
                  {area}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </>
  );
}
